import "./distortion-module.css";

import { Knob } from "./knob.ts";
import { Selector, type GlyphFn } from "./selector.ts";

// DistortionModule — pre-EQ saturator. Three curve types, drive in dB
// (0–24), wet/dry mix (0–1). The curve selector cells render the
// actual transfer characteristic each curve produces, so the user can
// see soft vs hard vs asymmetric at a glance.
//
// Engine paths:
//   fx.distortion.drive   — 0..24 dB
//   fx.distortion.mix     — 0..1 (dry to wet)
//   fx.distortion.curve   — discrete 0=Soft, 1=Hard, 2=Asymmetric

export type DistortionCurveId = "soft" | "hard" | "asymmetric";

const CURVE_ENGINE_INDEX: Record<DistortionCurveId, number> = {
  soft: 0,
  hard: 1,
  asymmetric: 2,
};

export interface DistortionState {
  driveDb: number;
  mix: number;
  curve: DistortionCurveId;
}

export interface DistortionModuleOptions {
  container: HTMLElement;
  setParam: (path: string, value: number) => void;
  setDiscrete: (path: string, value: number) => void;
  initial?: Partial<DistortionState>;
}

export class DistortionModule {
  private root: HTMLDivElement;
  private driveKnob!: Knob;
  private mixKnob!: Knob;
  private curveSelector!: Selector<DistortionCurveId>;

  constructor(opts: DistortionModuleOptions) {
    const init = opts.initial ?? {};
    const driveDb = init.driveDb ?? 0;
    const mix = init.mix ?? 0;
    const curve = init.curve ?? "soft";

    this.root = document.createElement("div");
    this.root.className = "distortion-module";

    // Header: title + 3-cell curve selector.
    const header = document.createElement("div");
    header.className = "distortion-module-header fx-module-header";
    const title = document.createElement("span");
    title.className = "distortion-module-title fx-module-title";
    title.textContent = "distortion";
    header.append(title);

    const curveHost = document.createElement("div");
    curveHost.className = "distortion-module-curve";
    header.append(curveHost);
    this.curveSelector = new Selector<DistortionCurveId>({
      container: curveHost,
      options: [
        { value: "soft", glyph: drawSoftCurve, label: "soft (tanh)" },
        { value: "hard", glyph: drawHardCurve, label: "hard clip" },
        { value: "asymmetric", glyph: drawAsymCurve, label: "asymmetric" },
      ],
      value: curve,
      onChange: (v) => {
        opts.setDiscrete("fx.distortion.curve", CURVE_ENGINE_INDEX[v]);
      },
    });
    this.root.append(header);

    // Knob row.
    const knobs = document.createElement("div");
    knobs.className = "distortion-module-knobs";
    this.root.append(knobs);

    this.driveKnob = new Knob({
      container: knobs,
      size: 44,
      min: 0,
      max: 24,
      default: 0,
      value: driveDb,
      label: "drive",
      format: (v) => `${v.toFixed(1)} db`,
      onChange: (v) => opts.setParam("fx.distortion.drive", v),
    });
    this.mixKnob = new Knob({
      container: knobs,
      size: 44,
      min: 0,
      max: 1,
      default: 0,
      value: mix,
      label: "mix",
      format: (v) => `${Math.round(v * 100)}%`,
      onChange: (v) => opts.setParam("fx.distortion.mix", v),
    });

    opts.container.append(this.root);
  }

  getState(): DistortionState {
    return {
      driveDb: this.driveKnob.current,
      mix: this.mixKnob.current,
      curve: this.curveSelector.getValue(),
    };
  }

  setState(s: DistortionState): void {
    this.driveKnob.setValue(s.driveDb);
    this.mixKnob.setValue(s.mix);
    this.curveSelector.select(s.curve);
  }

  destroy(): void {
    this.root.remove();
  }
}

// ----- curve glyphs -----
//
// Each glyph draws the actual transfer function (input on X, output on
// Y) the engine implements for that curve. Small enough to read as an
// icon at the selector's 36-px cell size.

const drawSoftCurve: GlyphFn = (ctx, size) => {
  const inset = 8;
  const w = size - 2 * inset;
  const cx = size / 2;
  const cy = size / 2;
  ctx.beginPath();
  // tanh-ish s-curve, 30 segments across the cell.
  const steps = 30;
  for (let i = 0; i <= steps; i++) {
    const u = i / steps; // 0..1
    const xn = (u - 0.5) * 2; // -1..1
    const yn = Math.tanh(xn * 2.2); // soft saturation
    const x = cx + xn * (w / 2);
    const y = cy - yn * (w / 2);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.stroke();
};

const drawHardCurve: GlyphFn = (ctx, size) => {
  const inset = 8;
  const w = size - 2 * inset;
  const cx = size / 2;
  const cy = size / 2;
  // Hard clip: linear up to threshold ±0.6, flat at the rails.
  ctx.beginPath();
  const thr = 0.6;
  // Bottom-left flat segment.
  ctx.moveTo(cx - w / 2, cy + w / 2);
  ctx.lineTo(cx - thr * (w / 2), cy + w / 2);
  // Linear ramp.
  ctx.lineTo(cx + thr * (w / 2), cy - w / 2);
  // Top-right flat segment.
  ctx.lineTo(cx + w / 2, cy - w / 2);
  ctx.stroke();
};

const drawAsymCurve: GlyphFn = (ctx, size) => {
  const inset = 8;
  const w = size - 2 * inset;
  const cx = size / 2;
  const cy = size / 2;
  // Asymmetric: positive half soft-saturates harder than negative,
  // producing even-harmonic content.
  ctx.beginPath();
  const steps = 30;
  for (let i = 0; i <= steps; i++) {
    const u = i / steps;
    const xn = (u - 0.5) * 2;
    const yn = xn >= 0 ? Math.tanh(xn * 3.5) : Math.tanh(xn * 1.5);
    const x = cx + xn * (w / 2);
    const y = cy - yn * (w / 2);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.stroke();
};
